import { designService } from '@paragon/data-connections';

import type { AppDispatch, RootState } from '../store';
import {
  addChatEntry,
  appendMessages,
  assistantStore,
  getChat,
  proposePlan,
  setRunning,
  shouldStopChat,
  updateChatEntry,
} from './assistantStore';
import { Attachment, buildUserContent } from './attachments';
import { describeChangesSinceLastSeen, rememberOpenTruss, rememberWhatWasSeen } from './designerChanges';
import { runTurn } from './loop';
import { preparePreview } from './planPreview';
import { getAssistantSnapshot } from './snapshot';
import { findAssistantTool, getToolDefinitions } from './tools';
import { runTool } from './tools/runTool';

/**
 * Describes a failed request to DesignServer for the chat. The design app's connections throw the response body, which
 * for these routes is ASP.NET's problem details: the status code and, where the server gave one, why.
 */
export function describeRequestFailure(error: unknown) {
  const problem = (typeof error === 'object' && error !== null ? error : {}) as { status?: unknown; detail?: unknown };
  const detail = typeof problem.detail === 'string' ? problem.detail.slice(0, 300) : null;

  switch (problem.status) {
    case 401:
      return 'Your session has expired. Sign in again to use the Assistant.';
    case 403:
    case 404:
      return 'The Assistant isn’t available for this account or on this server.';
    case 413:
      return 'This conversation is too large to send, probably because of its attachments. Start a new conversation.';
    case 429:
      return detail ?? 'You’ve reached the Assistant’s limit for today.';
    case 504:
      return detail ?? 'The model took too long to answer. Try again.';
  }

  if (typeof problem.status === 'number')
    return `The Assistant couldn’t reach its model (${problem.status}${detail ? `: ${detail}` : ''}).`;
  return 'The Assistant couldn’t reach DesignServer. Check your connection and try again.';
}

let toolQueue: Promise<unknown> = Promise.resolve();

/**
 * Runs tool calls from all the chat windows one at a time. Tools open trusses and switch views, so two running at once
 * could each act on what the other opened. The windows' models still think in parallel.
 */
function oneToolAtATime<T>(call: () => Promise<T>): Promise<T> {
  const result = toolQueue.then(call);
  toolQueue = result.catch(() => {});
  return result;
}

/**
 * Runs one Turn of the Assistant against the app in one chat window: the designer's message, the tools the model asks
 * for, and its reply, shown in the window as they happen.
 */
export async function sendAssistantMessage(
  chatId: string,
  text: string,
  attachments: readonly Attachment[],
  dispatch: AppDispatch,
  getState: () => RootState,
) {
  const trimmed = text.trim() || (attachments.length > 0 ? 'See the attached files.' : '');
  const chat = getChat(chatId);
  if (trimmed === '' || chat == null || chat.running) return;

  const { conversationGuid, messages, model } = chat;
  const turnGuid = crypto.randomUUID();
  const toolEntryIndices = new Map<string, number>();
  let reply = '';
  // The entry showing the model's reply while it streams in, which its whole reply then replaces.
  let streamingEntryIndex: number | null = null;

  setRunning(chatId, true);
  addChatEntry(chatId, {
    type: 'user',
    text: trimmed,
    ...(attachments.length > 0 ? { attachments: attachments.map(({ name, kind }) => ({ name, kind })) } : {}),
  });

  // What the designer changed since the Assistant's last tool call, shown with every request this Turn.
  const changedSinceYourLastTurn = describeChangesSinceLastSeen('assistant', getState());
  rememberWhatWasSeen('assistant', getState());

  try {
    const end = await runTurn(messages, buildUserContent(trimmed, attachments), {
      conversationGuid,
      turnGuid,
      tools: getToolDefinitions(),
      complete: (request) => {
        streamingEntryIndex = null;
        let streamed = '';
        return designService.assistant.complete({ ...request, model }, (text) => {
          streamed += text;
          if (streamingEntryIndex != null)
            updateChatEntry(chatId, streamingEntryIndex, { type: 'assistant', text: streamed });
          else if (streamed.trim() !== '')
            streamingEntryIndex = addChatEntry(chatId, { type: 'assistant', text: streamed });
        });
      },
      getSnapshot: () => ({
        ...getAssistantSnapshot(getState()),
        ...(changedSinceYourLastTurn == null ? {} : { changedSinceYourLastTurn }),
      }),
      shouldStop: () => shouldStopChat(chatId),
      proposePlan: async (plan, previewRequest) => {
        // Show the truss or layout the Plan changes, and draw the changes there while the designer decides.
        const preview =
          previewRequest == null
            ? null
            : await oneToolAtATime(() =>
                preparePreview(previewRequest, dispatch, getState, !assistantStore.getState().autoApprovePlans),
              );
        // The preview may open the truss it changes; that's the Assistant's doing, not the designer's.
        rememberOpenTruss('assistant', getState());
        return proposePlan(chatId, { ...plan, preview });
      },
      runTool: (name, input, planApproved) => {
        const tool = findAssistantTool(name);
        if (tool == null) return Promise.resolve({ ok: false, error: `There is no tool named "${name}".` });
        return oneToolAtATime(() =>
          runTool(tool, input, {
            caller: 'assistant',
            dispatch,
            getState,
            planApproved,
            chatId,
          }),
        );
      },
      onMessages: (newMessages) => {
        appendMessages(chatId, ...newMessages);
        for (const message of newMessages)
          if (message.role === 'assistant' && typeof message.content === 'string' && message.content.trim() !== '') {
            if (streamingEntryIndex != null)
              updateChatEntry(chatId, streamingEntryIndex, { type: 'assistant', text: message.content });
            else addChatEntry(chatId, { type: 'assistant', text: message.content });
            streamingEntryIndex = null;
            reply = message.content;
          }
      },
      onToolStarted: (call) => {
        toolEntryIndices.set(
          call.id,
          addChatEntry(chatId, { type: 'tool', toolName: call.function.name, status: 'running' }),
        );
      },
      onToolFinished: (call, outcome) => {
        const index = toolEntryIndices.get(call.id);
        if (index == null) return;
        updateChatEntry(chatId, index, {
          type: 'tool',
          toolName: call.function.name,
          status: outcome.ok ? 'done' : 'failed',
          ...(outcome.ok ? {} : { detail: outcome.error }),
        });
      },
    });

    if (end === 'stopped') addChatEntry(chatId, { type: 'notice', text: 'Stopped.' });
  } catch (error) {
    console.error(error);
    addChatEntry(chatId, { type: 'notice', text: describeRequestFailure(error) });
    // A failed request leaves the conversation where the model last saw it, so the designer can try again.
  } finally {
    setRunning(chatId, false);
    // Every Turn can be rated, including one that failed or was stopped.
    addChatEntry(chatId, {
      type: 'feedback',
      guid: crypto.randomUUID(),
      turnGuid,
      request: trimmed,
      reply,
      rating: null,
      comment: '',
      recording: null,
      status: 'unsent',
    });
  }
}
